import PlannerService from 'services/PlannerService'
import { posthogClient } from 'plugins/posthog'
import { toast } from 'utils/toast'
import { daysByDate } from 'utils/planner/days'

// Training Planner (beta) — isolated store module. Plan state lives on the
// server; every operation answers with the fresh plan, so the module only
// ever replaces `plan` wholesale. Nothing here is persisted to IndexedDB.
const types = {
  PLAN_SET: 'PLAN_SET',
  PREFILL_SET: 'PREFILL_SET',
  REVIEW_SET: 'REVIEW_SET',
  CHANGES_SET: 'CHANGES_SET',
  LOADING_SET: 'LOADING_SET',
  BUSY_SET: 'BUSY_SET',
  ERROR_SET: 'ERROR_SET',
  LAST_RESULT_SET: 'LAST_RESULT_SET',
  DEVICES_SET: 'DEVICES_SET'
}

const state = {
  plan: null,
  // { intervals: {…}, stridee: { connected, providers, … } } — kept apart
  // from the plan so Settings → Connections can show it without one.
  devices: null,
  prefill: null,
  // The plan that just ended: its numbers and the settings to seed the next one.
  review: null,
  changes: [],
  loaded: false,
  loading: false,
  busy: false,
  error: null,
  lastResult: null
}

const capture = (event, properties = {}) => posthogClient()?.capture(event, { ...properties, beta: true })

const actions = {
  async load({ commit }) {
    commit(types.LOADING_SET, true)
    commit(types.ERROR_SET, null)
    try {
      const data = await PlannerService.load()
      commit(types.PLAN_SET, data.plan)
      if (data.prefill) commit(types.PREFILL_SET, data.prefill)
      commit(types.REVIEW_SET, data.review || null)
    } catch (error) {
      // 403 is the normal state for someone not yet in the beta: they see the
      // pitch and the waitlist, not an error.
      if (error.response?.status === 403) {
        commit(types.PLAN_SET, null)
      } else {
        commit(types.ERROR_SET, error.response?.data?.error || 'planner_unavailable')
      }
    } finally {
      commit(types.LOADING_SET, false)
    }
  },

  async loadPrefill({ commit }) {
    const prefill = await PlannerService.prefill()
    commit(types.PREFILL_SET, prefill)
    return prefill
  },

  async createPlan({ commit }, payload) {
    commit(types.BUSY_SET, true)
    try {
      const plan = await PlannerService.create(payload)
      commit(types.PLAN_SET, plan)
      commit(types.REVIEW_SET, null)
      capture('plan_created', { event_type: plan.event?.type, goal_focus: plan.goal_focus })
      toast('Your plan is ready')
      return plan
    } finally {
      commit(types.BUSY_SET, false)
    }
  },

  async endPlan({ commit }) {
    const data = await PlannerService.end()
    commit(types.PLAN_SET, null)
    if (data.prefill) commit(types.PREFILL_SET, data.prefill)
    commit(types.REVIEW_SET, data.review || null)
    capture('plan_ended')
    toast('Plan ended')
  },

  // Every session/plan edit funnels through here so the result handling is
  // identical: replace the plan, toast the message, capture the op.
  async operate({ commit }, { op, id, payload, silent }) {
    commit(types.BUSY_SET, true)
    try {
      let result
      switch (op) {
        case 'move':
          result = await PlannerService.move(id, payload.to_date)
          break
        case 'swap':
          result = await PlannerService.swap(id, payload)
          break
        case 'skip':
          result = await PlannerService.skip(id)
          break
        case 'unskip':
          result = await PlannerService.unskip(id)
          break
        case 'complete':
          result = await PlannerService.complete(id)
          break
        case 'remove':
          result = await PlannerService.remove(id)
          break
        case 'rearrange':
          result = await PlannerService.rearrange(id)
          break
        case 'lock':
          result = await PlannerService.lock(id, payload.locked)
          break
        case 'difficulty':
          result = await PlannerService.difficulty(id, payload.difficulty, payload.cause)
          break
        case 'add_session':
          result = await PlannerService.addSession(payload)
          break
        case 'add_adjustment':
          result = await PlannerService.addAdjustment(payload)
          break
        case 'remove_adjustment':
          result = await PlannerService.removeAdjustment(id)
          break
        case 'update_settings':
          result = await PlannerService.updateSettings(payload)
          break
        case 'regenerate':
          result = await PlannerService.regenerate(payload?.scope)
          break
        case 'undo':
          result = await PlannerService.undo(id)
          break
        case 'apply_proposal':
          result = await PlannerService.applyProposal(id, payload?.scope)
          break
        case 'discard_proposal':
          result = await PlannerService.discardProposal(id)
          break
        default:
          throw new Error(`Unknown planner operation ${op}`)
      }

      if (result.plan) commit(types.PLAN_SET, result.plan)
      commit(types.LAST_RESULT_SET, { op, ...result })
      capture('plan_session_edited', { op, ok: result.ok, violation: result.violations?.[0]?.rule })

      if (!silent) {
        if (result.ok) toast(result.message || 'Done')
        else toast(result.violations?.[0]?.message || result.message || result.detail || 'That change is not possible', { type: 'error', duration: 6000 })
      }
      return result
    } catch (error) {
      toast(error.response?.data?.detail || 'Something went wrong. Please try again.', { type: 'error' })
      throw error
    } finally {
      commit(types.BUSY_SET, false)
    }
  },

  async loadChanges({ commit }) {
    const changes = await PlannerService.changes()
    commit(types.CHANGES_SET, changes)
    return changes
  },

  async sendFeedback(_, { context, message }) {
    await PlannerService.feedback(context, message)
    capture('plan_feedback_sent', { context })
    toast('Thanks, your feedback is in')
  },

  // Device sync, down either road. Every answer's `devices` replaces the state.
  async loadDevices({ commit }) {
    try {
      const devices = await PlannerService.devices()
      commit(types.DEVICES_SET, devices)
      return devices
    } catch (error) {
      // 403 = not in the beta; there is nothing to show.
      return null
    }
  },

  async connectIntervals({ commit }, payload) {
    commit(types.BUSY_SET, true)
    try {
      const result = await PlannerService.connectIntervals(payload)
      if (result.devices) commit(types.DEVICES_SET, result.devices)
      if (result.ok) {
        capture('plan_device_connected', { provider: 'intervals' })
        toast('intervals.icu connected. Your sessions are on the calendar.')
      }
      return result
    } catch (error) {
      toast(error.response?.data?.detail || 'intervals.icu could not be reached. Try again in a minute.', { type: 'error' })
      throw error
    } finally {
      commit(types.BUSY_SET, false)
    }
  },

  async disconnectIntervals({ commit }) {
    commit(types.BUSY_SET, true)
    try {
      const result = await PlannerService.disconnectIntervals()
      if (result.devices) commit(types.DEVICES_SET, result.devices)
      capture('plan_device_disconnected', { provider: 'intervals' })
      toast('intervals.icu disconnected')
      return result
    } finally {
      commit(types.BUSY_SET, false)
    }
  },

  // The test runs without the global busy flag: the rest of the plan stays
  // usable while the rider goes off to look at their watch.
  async testIntervals() {
    try {
      const result = await PlannerService.testIntervals()
      if (result.ok) capture('plan_device_test_sent', { provider: 'intervals' })
      else toast(result.detail || 'The test session could not be sent.', { type: 'error', duration: 6000 })
      return result
    } catch (error) {
      // A 502 from an unreachable intervals.icu throws rather than unwrapping,
      // and the button must never be left saying "sending".
      toast(error.response?.data?.detail || 'The test session could not be sent.', { type: 'error', duration: 6000 })
      return { ok: false }
    }
  },

  async answerIntervalsTest(_ctx, reached) {
    capture('plan_device_test_answered', { provider: 'intervals', reached: !!reached })
    try {
      return await PlannerService.clearIntervalsTest(reached)
    } catch (error) {
      return { ok: false }
    }
  },

  async syncDevices({ commit }) {
    commit(types.BUSY_SET, true)
    try {
      const result = await PlannerService.syncDevices()
      if (result.devices) commit(types.DEVICES_SET, result.devices)
      if (result.ok) toast('Your device is up to date')
      else {
        const failed = result.devices?.stridee?.last_error || result.devices?.intervals?.last_error
        toast(failed || result.detail || 'Sync failed', { type: 'error', duration: 6000 })
      }
      return result
    } finally {
      commit(types.BUSY_SET, false)
    }
  },

  // Stridee (Garmin, Wahoo, COROS). Connecting leaves the app for their hosted
  // page, so this dispatch ends in a redirect and the answer arrives later,
  // through refreshStridee.
  async connectStridee({ commit }, { provider, returnTo, consent }) {
    commit(types.BUSY_SET, true)
    try {
      const result = await PlannerService.connectStridee(provider, returnTo, consent)
      if (result.ok && result.connect_url) {
        capture('plan_device_connect_started', { provider: 'stridee', device: provider })
        window.location.assign(result.connect_url)
      }
      return result
    } catch (error) {
      toast(error.response?.data?.detail || 'Your device service could not be reached. Try again in a minute.', { type: 'error' })
      return { ok: false }
    } finally {
      commit(types.BUSY_SET, false)
    }
  },

  // The rider is back from the hosted page. Silent about failure: they may
  // simply have changed their mind, and the device list says what happened.
  async refreshStridee({ commit }) {
    try {
      const result = await PlannerService.refreshStridee()
      if (result.devices) commit(types.DEVICES_SET, result.devices)
      if (result.devices?.stridee?.connected) {
        capture('plan_device_connected', { provider: 'stridee' })
        toast('Connected. Your planned sessions are on their way to your device.')
      }
      return result
    } catch (error) {
      return { ok: false }
    }
  },

  async disconnectStridee({ commit }, provider) {
    commit(types.BUSY_SET, true)
    try {
      const result = await PlannerService.disconnectStridee(provider)
      if (result.devices) commit(types.DEVICES_SET, result.devices)
      capture('plan_device_disconnected', { provider: 'stridee', device: provider })
      toast('Disconnected. The planned sessions are off your device.')
      return result
    } finally {
      commit(types.BUSY_SET, false)
    }
  },

  // The week 2 / week 4 survey: three answers and a note, then the plan is
  // reloaded so the card goes away.
  async sendSurvey({ dispatch }, { week, answers, message }) {
    await PlannerService.survey({ week, answers, message })
    capture('plan_feedback_sent', { context: 'survey', week })
    toast('Thanks, that helps')
    await dispatch('load')
  }
}

const mutations = {
  [types.PLAN_SET](state, plan) {
    state.plan = plan ? Object.freeze(plan) : null
    state.loaded = true
    if (plan && plan.devices) state.devices = Object.freeze(plan.devices)
  },
  [types.REVIEW_SET](state, review) {
    state.review = review ? Object.freeze(review) : null
  },
  [types.PREFILL_SET](state, prefill) {
    state.prefill = prefill ? Object.freeze(prefill) : null
  },
  [types.CHANGES_SET](state, changes) {
    state.changes = changes
  },
  [types.LOADING_SET](state, loading) {
    state.loading = loading
  },
  [types.BUSY_SET](state, busy) {
    state.busy = busy
  },
  [types.ERROR_SET](state, error) {
    state.error = error
  },
  [types.LAST_RESULT_SET](state, result) {
    state.lastResult = result
  },
  [types.DEVICES_SET](state, devices) {
    state.devices = devices ? Object.freeze(devices) : null
    if (state.plan) state.plan = Object.freeze({ ...state.plan, devices })
  }
}

const getters = {
  plan: (state) => state.plan,
  devices: (state) => state.devices,
  prefill: (state) => state.prefill,
  review: (state) => state.review,
  changes: (state) => state.changes,
  loaded: (state) => state.loaded,
  loading: (state) => state.loading,
  busy: (state) => state.busy,
  error: (state) => state.error,
  lastResult: (state) => state.lastResult,
  days: (state) => (state.plan ? state.plan.weeks.flatMap((w) => w.days) : []),
  // The same row per date as the week strip and the calendar draw (utils/planner/days).
  byDate: (state) => daysByDate(state.plan && state.plan.weeks),
  today: (state, getters) => (state.plan && getters.byDate[state.plan.today]) || null,
  dayByDate: (state, getters) => (date) => getters.byDate[date] || null,
  committedDays: (state, getters) => getters.days.filter((d) => d.committed && d.date >= (state.plan?.today || ''))
}

export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters
}
